import { z } from "zod";
import { questionAnswerRefSchema } from "./agent-questions.ts";
import { taskProposalViewSchema } from "./agent-tasks.ts";
import { ATTACHMENT_LIMITS, attachmentIdSchema } from "./attachments.ts";
import { folderIdSchema, optionalFolderIdSchema } from "./folders.ts";
import {
  documentIdSchema,
  readerSchema,
  storedDocumentOriginSchema,
  storedTrustSchema,
} from "./knowledge.ts";
import { memoryProposalViewSchema } from "./memory.ts";
import { effortSchema, turnOptionsSchema } from "./model-choice.ts";
import {
  conversationSchema,
  messageIdSchema,
  messageRoleSchema,
  projectIdSchema,
  projectRoleSchema,
} from "./projects.ts";
import { userIdSchema } from "./tenancy.ts";
import { wikiSuggestionViewSchema } from "./wiki-suggestions.ts";

/**
 * Project conversations (specs `projects` "Persisted project conversations", `information-flow`).
 * The server owns the history: a turn request carries only the new user message, and anything
 * else the client sends (earlier messages, assistant or tool parts) is ignored.
 */

/** Shown in place of a title the viewer may not see (they fail the conversation's label). */
export const UNTITLED_CONVERSATION = "Untitled conversation";

export const MAX_MESSAGE_CHARS = 32_000;

/** A user message: text parts only (file and tool parts are refused). */
export const userMessageInputSchema = z.object({
  id: z.string().max(200).optional(),
  role: z.literal("user"),
  parts: z
    .array(z.object({ type: z.literal("text"), text: z.string().min(1).max(MAX_MESSAGE_CHARS) }))
    .min(1)
    .max(20),
});
export type UserMessageInput = z.infer<typeof userMessageInputSchema>;

/**
 * The AI SDK chat transport body: the server keeps only the last message, which must be the
 * user's new message. Bearer clients send `{ messages: [message] }`. `options` is the picker's
 * effort and model (spec `model-choice`), stored on the conversation and re-resolved against the
 * policy every turn; absent (older clients), the stored choice applies.
 */
export const sendMessageRequestSchema = z.object({
  messages: z.array(z.unknown()).min(1).max(1000),
  options: turnOptionsSchema.optional(),
  /**
   * Ready attachments of this conversation sent with the message (conversation-attachments),
   * each at most once in the conversation. Optional: an API one release behind ignores it.
   */
  attachments: z
    .array(attachmentIdSchema)
    .max(ATTACHMENT_LIMITS.perMessage)
    .refine((ids) => new Set(ids.map((id) => id.toLowerCase())).size === ids.length, {
      message: "attachments must be unique",
    })
    .optional(),
  /**
   * The question this message answers (agent-turn D7), by its tool call: the server binds it
   * only to a question of the same conversation and ignores any other reference. Optional: an
   * API one release behind ignores it.
   */
  questionAnswer: questionAnswerRefSchema.optional(),
});

/**
 * A message as rendered for one viewer. `hidden`: the viewer fails its label, so `parts` is
 * empty and nothing about its sources is revealed.
 */
export const renderedMessageSchema = z.object({
  id: messageIdSchema,
  role: messageRoleSchema,
  parts: z.array(z.unknown()),
  trust: storedTrustSchema,
  hidden: z.boolean(),
  createdAt: z.iso.datetime(),
});
export type RenderedMessage = z.infer<typeof renderedMessageSchema>;

/** A conversation's project as its lists and header show it; null for a standalone one. */
export const conversationProjectSchema = z
  .object({ id: projectIdSchema, name: z.string() })
  .nullable();

/** Who shared a conversation with the viewer (recipients only). */
export const conversationSharerSchema = z
  .object({ userId: userIdSchema, name: z.string().nullable() })
  .nullable();

/**
 * A conversation's project share (openspec change `project-conversation-share`): when its
 * creator shared it with the project. Shown to the creator and to viewers it reaches; null when
 * not shared (or not shown to the viewer). Absent from responses of an API that predates it.
 */
export const conversationProjectShareSchema = z.object({ sharedAt: z.iso.datetime() }).nullable();
export type ConversationProjectShare = z.infer<typeof conversationProjectShareSchema>;

/**
 * The creator's view of the project share (`/api/conversations/:id/project-share`): when it was
 * shared (null when not), how many people it can reach (readers and above, the creator, guests,
 * support accounts and break-glass members aside), and how many of them can't read some of the
 * conversation's sources. Counts only: never which document or person.
 */
export const projectShareStateSchema = z.object({
  sharedAt: z.iso.datetime().nullable(),
  audience: z.int().min(0),
  excluded: z.int().min(0),
});
export type ProjectShareState = z.infer<typeof projectShareStateSchema>;

export const conversationDetailSchema = conversationSchema.extend({
  messages: z.array(renderedMessageSchema),
  /** Its project, or null for a standalone conversation. */
  project: conversationProjectSchema.optional(),
  /** For a recipient: who shared it. Null for its creator and break-glass viewers. */
  sharedBy: conversationSharerSchema.optional(),
  /** Its project share; absent from an API that predates it (a web app probes for it). */
  projectShare: conversationProjectShareSchema.optional(),
  /**
   * True when the viewer reaches it only through the project share: read-only, nothing can be
   * saved from it. Absent otherwise (and from an API that predates it).
   */
  viaProjectShare: z.boolean().optional(),
  /** Its creator's pending memory proposals (memory-personal), each under its message. */
  memoryProposals: z.array(memoryProposalViewSchema).optional(),
  /**
   * The creator's task proposals under the messages they see (agent-turn D11), any state;
   * optional for one release.
   */
  taskProposals: z.array(taskProposalViewSchema).optional(),
  /** Its creator's pending wiki suggestions (wiki-suggestions), each under its message. */
  wikiSuggestions: z.array(wikiSuggestionViewSchema).optional(),
  /** The creator's stored effort (spec `model-choice`); null when never chosen. */
  effort: effortSchema.nullable().optional(),
  /** The creator's stored Advanced model pick (a catalogue key); null when none. */
  preferredModel: z.string().nullable().optional(),
});
export type ConversationDetail = z.infer<typeof conversationDetailSchema>;

/**
 * Share recipients: users of the project by ID (project conversations, picked from the member
 * list), or tenant users by email address (standalone conversations: members have no
 * directory, so nothing is listed). Exactly one of the two.
 */
export const shareConversationRequestSchema = z
  .object({
    userIds: z.array(z.uuid()).min(1).max(50).optional(),
    emails: z.array(z.string().trim().toLowerCase().min(3).max(320)).min(1).max(20).optional(),
  })
  .refine((body) => (body.userIds === undefined) !== (body.emails === undefined), {
    message: "Give either userIds or emails",
  });
export type ShareConversationRequest = z.infer<typeof shareConversationRequestSchema>;

/**
 * Why a recipient was refused; the reason never names a source or a document title. A
 * recipient named by email is refused as `cannot_receive` whatever the cause (unknown,
 * disabled or another tenant's address), so nothing can be enumerated.
 */
export const shareRefusalSchema = z.object({
  /** The recipient's user ID, or the email address as given. */
  userId: z.string(),
  code: z.enum([
    "unknown_user",
    "cannot_read_project",
    "cannot_read_sources",
    "self",
    "cannot_receive",
  ]),
  reason: z.string(),
});
export type ShareRefusal = z.infer<typeof shareRefusalSchema>;

export const shareConversationResponseSchema = z.object({
  shared: z.array(userIdSchema),
  refused: z.array(shareRefusalSchema),
});
export type ShareConversationResponse = z.infer<typeof shareConversationResponseSchema>;

export const conversationShareSchema = z.object({
  userId: userIdSchema,
  /** The recipient's display name (else email): the creator named them. */
  name: z.string().nullable().optional(),
  sharedBy: userIdSchema,
  createdAt: z.iso.datetime(),
});
export type ConversationShare = z.infer<typeof conversationShareSchema>;

/** The most share candidates one request returns (a visible project can reach the tenant). */
export const SHARE_CANDIDATES_LIMIT = 200;

/**
 * An optional prefix of the name shown (#151); of an e-mail address too for callers who may
 * manage members.
 */
export const shareCandidatesQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
});

/**
 * One person a project conversation's creator may name (#151): someone who can read the project,
 * with their effective role. How they reach it (directly, a group, a rule, visibility) is not said.
 */
export const shareCandidateSchema = z.object({
  userId: userIdSchema,
  /** The display name, else the e-mail address, as the members list shows it. */
  displayName: z.string(),
  role: projectRoleSchema,
});
export type ShareCandidate = z.infer<typeof shareCandidateSchema>;

export const shareCandidatesSchema = z.object({
  candidates: z.array(shareCandidateSchema),
  /** More people match than {@link SHARE_CANDIDATES_LIMIT}: narrow with `q`. */
  truncated: z.boolean(),
});
export type ShareCandidates = z.infer<typeof shareCandidatesSchema>;

/**
 * A suggested title for saving an assistant message (spec `saved-answer-titles`): checked like
 * the save to `sink`; `targetProjectId` as for that save.
 */
export const saveTitleRequestSchema = z.object({
  sink: z.enum(["file", "wiki"]),
  targetProjectId: projectIdSchema.optional(),
});
export type SaveTitleRequest = z.infer<typeof saveTitleRequestSchema>;

export const saveTitleSuggestionSchema = z.object({
  title: z.string().max(200),
  /** `heuristic`: no model wrote it (failure, timeout, spend limit or a label the caller fails). */
  source: z.enum(["model", "heuristic"]),
});
export type SaveTitleSuggestion = z.infer<typeof saveTitleSuggestionSchema>;

export const saveMessageRequestSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  /**
   * The project to save into: required from a standalone conversation, refused (`400`) from a
   * project conversation, which saves into its own project.
   */
  targetProjectId: projectIdSchema.optional(),
  /**
   * "Publish with wider readers" (sink `save_file`, ADR 0009 amendment): the file's readers,
   * and the declassification reason. Without it, the file keeps its sources' readers.
   */
  declassify: z
    .object({
      readers: z.array(readerSchema).min(1).max(100).optional(),
      reason: z.string().max(2000),
    })
    .optional(),
  /** A Files folder of the target project; the Files root when absent or null. */
  folderId: optionalFolderIdSchema,
});

/** Who can read a file saved from a message right now, shown before confirming. */
export const savePreviewSchema = z.object({
  readers: z.array(z.object({ userId: userIdSchema, displayName: z.string().nullable() })),
  classification: z.string(),
  origin: storedDocumentOriginSchema,
  /** The declassification review data, when the caller may save with wider readers. */
  declassification: z
    .object({
      intendedReaderCount: z.int().min(0),
      sources: z.array(
        z.object({ id: documentIdSchema, title: z.string(), cannotRead: z.int().min(0) }),
      ),
    })
    .nullable()
    .optional(),
});
export type SavePreview = z.infer<typeof savePreviewSchema>;

export const savedFileSchema = savePreviewSchema.extend({
  documentId: z.string(),
  projectId: projectIdSchema,
  title: z.string(),
  /**
   * Where the file landed: the requested folder, or the Files root (null) when it was deleted
   * meanwhile.
   */
  folderId: folderIdSchema.nullable().optional(),
});
export type SavedFile = z.infer<typeof savedFileSchema>;

/**
 * The `data-spend-limit` part of an assistant message whose turn stopped early (`data.reason`):
 * the tenant's hard monthly spend limit was reached between two steps (`model_budget`, spec
 * `tenant-plans`), the turn spent `models.turnMaxUsd` (`turn_budget`) or what was left of the
 * user's monthly share of the limit (`user_budget`, `models.userMonthlySharePct`), the budget could not be
 * read (`unavailable`), or an MCP ask ran out of its
 * wall clock (`timeout`, openspec change `mcp-ask-project`). Stored with the message, so the
 * notice is still there when the conversation is reopened.
 */
export const SPEND_LIMIT_PART = "data-spend-limit";
export const SPEND_LIMIT_PART_ID = "spend-limit";

/**
 * A model reasoning part (AI SDK `reasoning` or `reasoning-file`), #412. Reasoning is derived
 * text without the answer's readability rules (it may quote a passage the reader cannot read),
 * so it never reaches a client and is never stored: the answer stream does not send it, the
 * persisted parts drop it, and a stored message read back drops any older one.
 */
export function isReasoningPart(part: unknown): boolean {
  if (typeof part !== "object" || part === null) {
    return false;
  }
  const type = (part as { type?: unknown }).type;
  return type === "reasoning" || type === "reasoning-file";
}

/**
 * `parts` without its reasoning parts ({@link isReasoningPart}). A value that is not an array
 * (a malformed stored row) is returned as it is: reading it fails or not as it did before.
 */
export function withoutReasoning(parts: readonly unknown[]): unknown[] {
  return Array.isArray(parts) ? parts.filter((p) => !isReasoningPart(p)) : (parts as unknown[]);
}

/**
 * The target of a save preview from a standalone conversation (`?targetProjectId=`), and what
 * the save creates (`?sink=`, wiki-suggestions): `wiki` checks `wiki.draft` instead of
 * `file.save_answer`; absent, a file save as before.
 */
export const savePreviewQuerySchema = z.object({
  targetProjectId: z.uuid().optional(),
  sink: z.enum(["file", "wiki"]).optional(),
});

// ── Conversation management (spec `conversations`) ────────────────────────────────────────

export const CONVERSATION_TITLE_MAX = 200;

export const renameConversationRequestSchema = z.object({
  title: z.string().trim().min(1).max(CONVERSATION_TITLE_MAX),
});

/** `shared`: named shares with the caller; `project`: shared with the project, theirs included. */
export const conversationFilterSchema = z.enum(["all", "mine", "shared", "archived", "project"]);
export type ConversationFilter = z.infer<typeof conversationFilterSchema>;

export const CONVERSATION_PAGE_MAX = 50;
export const CONVERSATION_QUERY_MIN = 2;
export const CONVERSATION_QUERY_MAX = 200;

/**
 * Lists and search of the caller's conversations (`GET /api/me/conversations`, and a project's
 * `GET /api/projects/:projectId/conversations`, which ignores `projectId`). The query is never
 * logged, audited, stored or sent to a model. `cursor` is opaque.
 */
export const conversationListQuerySchema = z.object({
  q: z.string().trim().min(CONVERSATION_QUERY_MIN).max(CONVERSATION_QUERY_MAX).optional(),
  filter: conversationFilterSchema.default("all"),
  projectId: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(CONVERSATION_PAGE_MAX).default(25),
  cursor: z.string().min(1).max(200).optional(),
});
export type ConversationListQuery = z.infer<typeof conversationListQuerySchema>;

/** The label summary of a listed conversation, for a viewer who passes its label. */
export const conversationLabelSummarySchema = z.object({
  /** Its trust is `external`: an answer used web results. */
  webResults: z.boolean(),
  /** The most sensitive classification of its sources when above the tenant default. */
  level: z.string().nullable(),
  /** That level's display label. */
  levelLabel: z.string().nullable(),
});
export type ConversationLabelSummary = z.infer<typeof conversationLabelSummarySchema>;

/** Where a search query matched one of the viewer's own questions. */
export const conversationSnippetSchema = z.object({
  messageId: messageIdSchema,
  /** Plain text, about 160 characters around the first match. */
  text: z.string(),
  /** `[start, end)` character offsets of the matches in `text`. */
  ranges: z.array(z.tuple([z.int().min(0), z.int().min(0)])),
});
export type ConversationSnippet = z.infer<typeof conversationSnippetSchema>;

export const conversationListItemSchema = conversationSchema.extend({
  project: conversationProjectSchema,
  /** For its creator: how many users it is shared with. Null for everyone else. */
  sharedWith: z.int().min(0).nullable(),
  sharedBy: conversationSharerSchema,
  /** Its project share, as in the detail; absent from an API that predates it. */
  projectShare: conversationProjectShareSchema.optional(),
  /** Null for a viewer who fails the conversation's label (its title is null too). */
  label: conversationLabelSummarySchema.nullable(),
  /** Search only: the matching question, when the match was not (only) the title. */
  snippet: conversationSnippetSchema.nullable(),
});
export type ConversationListItem = z.infer<typeof conversationListItemSchema>;

/**
 * One page, newest activity first. A page may hold fewer than `limit` items while
 * `nextCursor` is set: rows the viewer may not see are dropped after the query.
 */
export const conversationListResponseSchema = z.object({
  items: z.array(conversationListItemSchema),
  nextCursor: z.string().nullable(),
});
export type ConversationListResponse = z.infer<typeof conversationListResponseSchema>;

/** `GET /api/conversations/:id/move?projectId=`: what moving into that project would change. */
export const moveQuerySchema = z.object({ projectId: z.uuid() });

export const movePreviewSchema = z.object({
  project: z.object({ id: projectIdSchema, name: z.string() }),
  /** Recipients who cannot read the project: their shares are removed by the move. */
  lapsingShares: z.array(z.object({ userId: userIdSchema, name: z.string().nullable() })),
  /** The project's admins can reach it under audited emergency access. */
  breakGlass: z.literal(true),
  /** False when the conversation used global models and the project is EU-only. */
  residencyOk: z.boolean(),
  /** The project answers from its sources only: later turns follow that mode. */
  sourcesOnly: z.boolean(),
  /**
   * Its live attachments, which stay with the conversation and never enter the project's files
   * or retrieval (conversation-attachments). Absent from older servers.
   */
  attachmentCount: z.int().min(0).optional(),
  /**
   * Of them, those at a `named`-floor level: they stay private to the creator and the people
   * they named after the move. Absent from older servers.
   */
  privateAttachmentCount: z.int().min(0).optional(),
  /** Echoed by the move: a change since the preview refuses it (`409 move_changed`). */
  fingerprint: z.string(),
});
export type MovePreview = z.infer<typeof movePreviewSchema>;

export const moveConversationRequestSchema = z.object({
  projectId: z.uuid(),
  fingerprint: z.string().min(1).max(128),
});
